import { useCallback, useEffect, useState } from 'react';
import { ErrorState, LoadingState, ScreenNotBuiltState } from '@assist/ui';
import { useSession } from '../state/session.js';
import { Shell } from './Shell.js';
import { SignIn } from '../screens/SignIn.js';
import { Dashboard } from '../screens/Dashboard.js';
import { FindingsList } from '../screens/FindingsList.js';
import { FindingDetail } from '../screens/FindingDetail.js';
import { FieldAppOnly } from '../screens/FieldAppOnly.js';
import { PrivacyNotice } from '../screens/PrivacyNotice.js';
import { AcceptInvitation } from '../screens/AcceptInvitation.js';
import { PrivacySettings } from '../screens/PrivacySettings.js';
import { ProcessingRegister } from '../screens/ProcessingRegister.js';
import { Organisation } from '../screens/Organisation.js';
import { ReportFinding } from '../screens/ReportFinding.js';
import { VerificationQueue } from '../screens/VerificationQueue.js';
import { Locations } from '../screens/Locations.js';
import { Users, UserDetail } from '../screens/Users.js';
import { SearchResults } from '../screens/SearchResults.js';
import { DailyHuddle } from '../screens/DailyHuddle.js';
import { EscalationSpotlight } from '../screens/EscalationSpotlight.js';
import { PointsRules } from '../screens/PointsRules.js';
import { Reports } from '../screens/Reports.js';
import { Audits } from '../screens/Audits.js';
import { AccountSettings } from '../screens/AccountSettings.js';
import {
  ClientPortfolio,
  PlatformAudit,
  PlatformHealth,
  PlatformMembers,
  PlatformModules,
  PlatformTenantDetail,
  PlatformTenants,
} from '../screens/Platform.js';
import { isBuiltRoute, landingFor } from '../lib/landing.js';

/**
 * Router konsol.
 *
 * DUA PULUH SATU LAYAR, DAN TIDAK SATU PUN RUTE YANG DITAWARKAN TANPA LAYARNYA
 * --------------------------------------------------------------------------
 * `apps/console/src/lib/navigasi.test.ts` menegakkannya dari MANIFEST: setiap butir
 * `provides.nav` bersurface `console` atau `platform` wajib punya layar di `BUILT_ROUTES`,
 * dan rute pendaratan setiap peran wajib ada di navigasi peran itu. Jadi menambahkan butir
 * navigasi tanpa membangun layarnya memerahkan uji — yang adalah bentuk UAT D-15 pada skala
 * navigasi: sebuah menu yang menawarkan layar yang belum ada adalah tawaran yang tidak dapat
 * dipenuhi.
 *
 * TIGA PINTU TANPA SLOT NAVIGASI, mengikuti peta §4 apa adanya:
 *   · `/settings/account`    lewat blok pengguna di kaki bilah sisi
 *   · `/search`              lewat kotak cari di bilah atas
 *   · `/meetings/escalation` lewat layar Rapat
 *
 * `ScreenNotBuiltState` masih dipakai, dan pemakainya sekarang hanya SATU kelas rute: layar
 * milik delapan modul yang belum punya manifest (M04, M05, M06, M08, M09, M12, M13, M14).
 * Mereka tidak muncul di navigasi siapa pun — Registry tidak dapat menawarkan apa yang tidak
 * dideklarasikan — jadi seseorang hanya mencapainya dengan mengetik alamatnya sendiri.
 */
export function App() {
  const { status, t, errorCode, refresh, me } = useSession();
  const [route, setRoute] = useState<string>(() => window.location.pathname + window.location.search);

  const navigate = useCallback((next: string) => {
    window.history.pushState({}, '', next);
    setRoute(next);
  }, []);

  useEffect(() => {
    const onPop = () => setRoute(window.location.pathname + window.location.search);
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  if (status === 'loading') {
    return (
      <div className="boot">
        <LoadingState rows={3} label={t('state.loading.title')} />
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="boot">
        <ErrorState
          title={t('state.error.title')}
          body={t('state.error.body')}
          code={errorCode}
          retryLabel={t('app.retry')}
          onRetry={() => void refresh()}
        />
      </div>
    );
  }

  /**
   * TIGA rute yang dilayani SEBELUM sesi ada, dan ketiganya sengaja diperiksa di
   * atas cabang `anonymous`.
   *
   * Pasal 21 menuntut pemberitahuan SEBELUM pemrosesan: orang berada di layar masuk
   * ketika ia membaca pemberitahuan privasi, dan ia berada di layar terima undangan
   * ketika ia memutuskan menyetujuinya. Mengarahkan keduanya ke layar masuk lebih
   * dulu akan membuat pemberitahuan itu tidak dapat dibaca justru pada dua saat
   * ia paling dibutuhkan.
   *
   * Yang ketiga, `/processing-register`, ada di sini karena alasan yang sejenis tetapi
   * bukan sama: yang membacanya adalah organisasi yang sedang MEMUTUSKAN apakah akan
   * menandatangani perjanjian pemrosesan data (Pasal 51), dan pada saat itu ia belum
   * punya akun sama sekali.
   */
  const preAuthPath = route.split('?')[0];
  if (preAuthPath === '/privacy-notice') {
    return (
      <div className="boot">
        <PrivacyNotice tenantSlug={new URLSearchParams(window.location.search).get('tenant_slug')} />
      </div>
    );
  }
  if (preAuthPath === '/processing-register') {
    return (
      <div className="boot">
        <ProcessingRegister />
      </div>
    );
  }
  if (preAuthPath === '/accept-invitation') {
    const token = new URLSearchParams(window.location.search).get('token') ?? '';
    if (token === '') return <SignIn />;
    return <AcceptInvitation token={token} />;
  }

  if (status === 'anonymous') return <SignIn />;

  const [path, search] = route.split('?');
  const landing = landingFor({ actorKind: me?.actor_kind ?? '', roleCode: me?.role_code ?? null });
  const diAkar = path === '/' || path === '' || path === undefined;
  /**
   * Di akar, yang dirender adalah layar rute PENDARATAN — bukan Dasbor bagi semua orang.
   * Dua cacat UAT berpangkal pada baris ini: operator menerima Dasbor Manajer dengan bilah
   * sisi kosong (D-14), dan aktor PQM menerima layar galat palsu atas `403` yang benar
   * (D-10). `landingFor()` memutuskannya, dan `navigasi.test.ts` membuktikan keputusannya
   * ada di navigasi peran itu.
   */
  const mentah = diAkar && landing.kind === 'route' ? landing.route : (path ?? '/');
  /**
   * PENGALIHAN PERMANEN, dan mengapa ia sebuah TABEL alih-alih sebuah cabang `if`.
   *
   * `/settings/privacy` berhenti menjadi layar: perancang menggabungkannya menjadi BAGIAN
   * dari `/settings/account` (`188:1225`), dengan breadcrumb ke induknya dan nol butir
   * navigasi baru. Rute lamanya dipertahankan karena ia sudah pernah ada di tautan dan di
   * tangkapan layar acuan — sebuah alamat yang pernah dibagikan tidak boleh berhenti
   * bekerja.
   *
   * Ia dinormalkan DI SINI, sebelum satu pun cabang layar, dan bukan sebagai cabang router
   * tersendiri yang merender layar yang sama. Bedanya penting dan dapat diukur: gerbang
   * inventaris layar membaca setiap perbandingan rute di berkas ini sebagai LAYAR dan
   * menuntut acuan Figma untuknya. Sebuah pengalihan tidak pernah digambar siapa pun, jadi
   * menuntut acuan untuknya akan memaksa seseorang menuliskan alasan palsu. Sebagai tabel ia
   * dibaca gerbang sebagai PENGALIHAN — lihat `pengalihanApp()` di gerbangnya — sehingga
   * alamat lamanya tetap bekerja dan gerbangnya tetap jujur.
   */
  const PENGALIHAN: Record<string, string> = {
    '/settings/privacy': '/settings/account/privacy',
  };
  const efektif = PENGALIHAN[mentah] ?? mentah;

  const findingMatch = /^\/findings\/([^/]+)$/.exec(efektif);
  const userMatch = /^\/users\/([^/]+)$/.exec(efektif);
  const tenantMatch = /^\/platform\/tenants\/([^/]+)$/.exec(efektif);
  const query = new URLSearchParams(search ?? '');

  let screen;
  if (diAkar && landing.kind === 'field-app') {
    // Peta §5.1: OPR bekerja di aplikasi lapangan. Layar ini TIDAK lagi jalan buntu —
    // ia menautkan dua layar konsol yang memang miliknya (D-14).
    screen = <FieldAppOnly onNavigate={navigate} />;
  } else if (efektif === '/findings/new') {
    // Diperiksa SEBELUM `/findings/:code`: sebuah temuan berkode "new" tidak ada, dan
    // urutan yang terbalik akan memanggil `GET /v1/findings/new` lalu menampilkan
    // "tidak ditemukan" pada tombol utama alur 1.
    screen = (
      <ReportFinding
        onCreated={(code) => navigate(`/findings/${encodeURIComponent(code)}`)}
        onBack={() => navigate('/findings')}
      />
    );
  } else if (findingMatch?.[1]) {
    screen = (
      <FindingDetail code={decodeURIComponent(findingMatch[1])} onBack={() => navigate('/findings')} />
    );
  } else if (efektif === '/verification-queue') {
    screen = (
      <VerificationQueue onOpen={(code) => navigate(`/findings/${encodeURIComponent(code)}`)} />
    );
  } else if (efektif === '/findings') {
    screen = (
      <FindingsList
        initialQuery={search}
        onOpen={(code) => navigate(`/findings/${encodeURIComponent(code)}`)}
        onNavigate={navigate}
      />
    );
  } else if (efektif === '/audits') {
    screen = <Audits />;
  } else if (efektif === '/meetings/daily') {
    screen = (
      <DailyHuddle onOpenFinding={(code) => navigate(`/findings/${encodeURIComponent(code)}`)} />
    );
  } else if (efektif === '/meetings/escalation') {
    screen = (
      <EscalationSpotlight
        onOpenFinding={(code) => navigate(`/findings/${encodeURIComponent(code)}`)}
      />
    );
  } else if (efektif === '/reports') {
    screen = <Reports />;
  } else if (efektif === '/search') {
    screen = <SearchResults query={query.get('q') ?? ''} onOpen={navigate} />;
  } else if (efektif === '/locations') {
    screen = <Locations />;
  } else if (userMatch?.[1]) {
    screen = <UserDetail userId={decodeURIComponent(userMatch[1])} onBack={() => navigate('/users')} />;
  } else if (efektif === '/users') {
    screen = <Users onOpen={(id) => navigate(`/users/${encodeURIComponent(id)}`)} />;
  } else if (efektif === '/points') {
    screen = <PointsRules />;
  } else if (efektif === '/portfolio') {
    // Setelah berpindah konteks, sesinya berubah di SERVER: `refresh()` membacanya kembali
    // dan navigasi konsultan menjadi navigasi tenant yang ia dampingi.
    screen = (
      <ClientPortfolio
        onSwitched={() => {
          void refresh().then(() => navigate('/dashboard'));
        }}
      />
    );
  } else if (tenantMatch?.[1]) {
    screen = (
      <PlatformTenantDetail
        tenantId={decodeURIComponent(tenantMatch[1])}
        onBack={() => navigate('/platform/tenants')}
      />
    );
  } else if (efektif === '/platform/tenants') {
    screen = <PlatformTenants onOpen={(id) => navigate(`/platform/tenants/${encodeURIComponent(id)}`)} />;
  } else if (efektif === '/platform/memberships') {
    screen = <PlatformMembers />;
  } else if (efektif === '/platform/modules') {
    screen = <PlatformModules />;
  } else if (efektif === '/platform/audit') {
    screen = <PlatformAudit />;
  } else if (efektif === '/platform/health') {
    screen = <PlatformHealth />;
  } else if (efektif === '/settings/account') {
    screen = <AccountSettings onOpenPrivacy={() => navigate('/settings/account/privacy')} />;
  } else if (efektif === '/settings/account/privacy') {
    screen = <PrivacySettings onBack={() => navigate('/settings/account')} />;
  } else if (efektif === '/settings/organisation') {
    screen = <Organisation />;
  } else if (efektif === '/dashboard') {
    screen = <Dashboard onNavigate={navigate} />;
  } else if (!isBuiltRoute(efektif)) {
    screen = (
      <ScreenNotBuiltState
        title={t('state.notBuilt.title')}
        body={t('state.notBuilt.body')}
        whoCanHelp={t('state.notBuilt.whoCanHelp')}
      />
    );
  } else {
    screen = <Dashboard onNavigate={navigate} />;
  }

  return (
    <Shell current={efektif} onNavigate={navigate} onSearch={(q) => navigate(`/search?q=${encodeURIComponent(q)}`)}>
      {screen}
    </Shell>
  );
}
